import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%',
    }
}));

const LandOwners = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        landOwners: [],
        landOwner: {
            idland_owner: 0,
            name: ''
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Landowners';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/landOwners');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, landOwners: response?.data ?? [] }));
        } else {
            console.log('[LandOwners] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, landOwner: { ...prev.landOwner, [target.name]: target.value } }));

    const validateForm = (type) => {
        const { idland_owner, name } = state.landOwner;

        switch (type) {
            case 'update':
                const data = state.landOwners.filter(value => value.idland_owner === idland_owner);

                if (data[0]) {
                    const before = JSON.stringify(data[0]);
                    const after = JSON.stringify(state.landOwner);
                    if (before !== after) {
                        if (name.trim()) {
                            return true;
                        } else {
                            showToast('All fields is required', 'error');
                            return false;
                        }
                    } else {
                        showToast('Update some field', 'error');
                        return false;
                    }
                } else {
                    showToast('Select some land owner', 'error');
                    return false;
                }

            case 'store':

                if (name.trim()) {
                    return true;
                } else {
                    showToast('All fields is required', 'error');
                    return false;
                }

            case 'delete':
                if (idland_owner === 0) {
                    showToast('Select some land owner', 'error');
                    return false;
                }
                const cond = window.confirm('Are you sure you want to delete this land owner?');

                return Boolean(idland_owner && cond);
            default:
                break;
        }
    }

    const clear = () => {
        setState(prev => ({ ...prev, landOwner: { ...prev.landOwner, idland_owner: 0, name: '' } }));
    }

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/landOwner/store', state.landOwner);
            if (response.status === 200) {
                setState(prev => ({ ...prev, landOwners: response?.data ?? prev.landOwners }));
                showToast(response.message);
            } else {
                console.log('[LandOwners] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false;
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { idland_owner } = state.landOwner;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/landOwner/${idland_owner}/update`, state.landOwner);
            if (response.status === 200) {
                setState(prev => ({ ...prev, landOwners: response?.data ?? prev.landOwners }));
                showToast(response.message);
            } else {
                console.log('[LandOwners] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { idland_owner } = state.landOwner;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/landOwner/${idland_owner}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, landOwners: response?.data ?? prev.landOwners }));
                showToast(response.message);
            } else {
                console.log('[LandOwners] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/land_owners/${row.idland_owner}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'history', headerName: 'Actions', flex: 0.3, renderCell }
    ];

    if (state.loading) {
        return <Loader />;
    }
    if (!state.permissions.read_attr) {
        return <NotFound />;
    }

    return (
        <div className={classes.root}>
            <ViewTitle sedeFlag title='Landowners' />
            <div>
                <Grid container>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.landOwner.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='land_owners' id='idland_owner' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.landOwners}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idland_owner}
                    loading={state.loading}
                    onRowClick={({ row }) => setState(prev => ({ ...prev, landOwner: row }))}
                />
            </div>
        </div>
    );
}

export default LandOwners;